import { Box, Grid } from '@mui/material';
import { useSnackbar } from 'notistack';
import React from 'react';
import { useMutation, useQueryClient } from 'react-query';
import { useNavigate } from 'react-router-dom';
import { Urls, useApi } from '../../context';
import { QueryKeys } from '../../hooks';
import { CurrentUser, DataRoomConfirmation, Deal } from '../../models';
import { EnumUtils, FormikUtils } from '../../utils';
import { CerebroApiEndpoints } from '../../utils/api';
import { getApiResponseData } from '../../utils/axios';
import { CerebroDrawer } from '../CerebroDrawer';
import { CerebroHeading } from '../CerebroHeading';
import { CerebroLink } from '../CerebroLink';
import { CerebroPrimaryButton } from '../CerebroPrimaryButton';
import { CerebroSecondaryButton } from '../CerebroSecondaryButton';
import { CerebroText } from '../CerebroText';
import { CerebroCheckbox } from '../CerebroCheckbox';
import { CerebroLabel } from '../CerebroLabel';
import { CerebroForm } from '../CerebroForm';
import { Field, Formik, FormikErrors } from 'formik';
import { CerebroTextarea } from '../CerebroTextarea';
import { CerebroInput } from '../CerebroInput';

interface LaunchDealFormProps {
	dataRoomGuid: string | undefined;
	deal?: Deal | null | undefined;
	currentUser: CurrentUser;
	confirmation: DataRoomConfirmation | null | undefined;
	close: () => void;
}

export const LaunchDealForm: React.FC<LaunchDealFormProps> = ({
	dataRoomGuid,
	deal,
	currentUser,
	confirmation,
	close,
}) => {
	const api = useApi();
	const snackbar = useSnackbar();
	const navigate = useNavigate();
	const client = useQueryClient();
	const [launching, setLaunching] = React.useState<boolean>(false);
	const isDirectIntroProduct = !confirmation ? false : confirmation.isDirectIntroProduct;

	const launchDealMutation = useMutation(
		async ({ values }: DealSuccessStoryFormMutationVariables) => {
			setLaunching(true);
			const { data, error } = await getApiResponseData<void>(
				api.post(CerebroApiEndpoints.DataRooms.LaunchDeal(dataRoomGuid), values)
			);

			if (error) {
				throw new Error(error.response?.data);
			}

			return data;
		},
		{
			onSuccess: () => {
				client.invalidateQueries(QueryKeys.DataRoom.Root(dataRoomGuid));
				client.invalidateQueries(QueryKeys.DataRoom.Confirmation(dataRoomGuid));
				client.invalidateQueries(QueryKeys.Teasers.Root(dataRoomGuid));
				client.invalidateQueries(QueryKeys.Deal(deal?.guid));
				snackbar.enqueueSnackbar('Deal Launched.', { variant: 'success' });
				setLaunching(false);
				close();
			},
			onError: (error: Error) => {
				setLaunching(false);
				snackbar.enqueueSnackbar('There was a problem launching the deal.', { variant: 'error' });
			},
			onSettled: (_data, _error, { helpers }) => {
				helpers.setSubmitting(false);
			},
		}
	);

	const goToDocuments = () => navigate(`${dataRoomGuid}/${Urls.Documents.Root}`);

	return (
		<CerebroDrawer open={confirmation != null} onClose={() => close()} PaperProps={{ sx: { width: '850px' } }}>
			{isDirectIntroProduct && (
				<>
					<CerebroHeading size="h1" gutterBottom>
						Launching a DirectConnect / DirectIntro Deal
					</CerebroHeading>

					<Box>
						<CerebroText>Choosing to launch deal will cause the following:</CerebroText>
						<ul>
							<li>
								<CerebroText>
									The status of the deal will change to {EnumUtils.GetDealStatusDescription('Active')}
								</CerebroText>
							</li>
							<li>
								<CerebroText>
									The data room will <strong>NOT</strong> be opened: lenders will not be able to
									access the data room.
								</CerebroText>
							</li>
							<li>
								<CerebroText>
									{confirmation?.unsentTeasers} teasers will be sent to lenders: when a lender
									accepts, an introductory email will be sent to both the borrower and lender.
								</CerebroText>
							</li>
						</ul>
					</Box>
				</>
			)}

			{!isDirectIntroProduct && (
				<>
					<CerebroHeading size="h1" gutterBottom>
						Are you ready to launch this deal?
					</CerebroHeading>

					<Box>
						<CerebroText>Choosing to launch deal will cause the following:</CerebroText>
						<ul>
							<li>
								<CerebroText>
									The status of the deal will change to {EnumUtils.GetDealStatusDescription('Active')}
								</CerebroText>
							</li>
							<li>
								<CerebroText>The data room will be opened</CerebroText>
							</li>
							<li>
								<CerebroText>
									{confirmation?.unsentTeasers} teasers will be sent to the lenders
								</CerebroText>
							</li>
							<li>
								<CerebroText>The following email will be sent to the borrower</CerebroText>
							</li>
						</ul>
					</Box>

					<br />

					<CerebroHeading size="h3">Subject</CerebroHeading>
					<CerebroText>{confirmation?.companyName}&quot;s Loan Request Status: Launched</CerebroText>
					<br />
					<CerebroHeading size="h3">Body</CerebroHeading>
					<CerebroText>{'{borrowerFirstName}'},</CerebroText>
					<CerebroText>
						We are excited to share that your loan request is live and lenders have been invited to bid on
						your loan request.
					</CerebroText>
					<br />

					<CerebroText>
						As discussed, I will be managing your loan request and will be in contact throughout the
						process. You can access and review the secure dataroom{' '}
						<CerebroLink onClick={goToDocuments}>HERE</CerebroLink>.
					</CerebroText>
					<CerebroText>
						I look forward to working with you and please contact me if you have any questions.
					</CerebroText>
					<br />

					{confirmation?.hasCompanySubscription && confirmation?.isNextAdditionalDeal && (
						<CerebroText>
							{confirmation.dealsLaunched} deals have been used on this subscription this year. This deal
							will incur the additional deal fee.
						</CerebroText>
					)}

					<br />
					<CerebroText>Thank you,</CerebroText>
					<CerebroText>
						{currentUser.firstName} {currentUser.lastName}
					</CerebroText>
					<CerebroText>Cerebro Capital</CerebroText>
				</>
			)}

			<br />
			<Formik
				enableReinitialize
				initialValues={{
					dealName: deal?.dealName ?? deal?.companyName ?? '',
					topDealChallenge1: confirmation?.topDealChallenge1 ?? '',
					topDealChallenge2: confirmation?.topDealChallenge2 ?? '',
					topDealChallenge3: confirmation?.topDealChallenge3 ?? '',
					isVerified: false,
				}}
				validate={validateForm}
				onSubmit={FormikUtils.MutateOnSubmit(launchDealMutation.mutate)}
			>
				{({ values, isSubmitting, handleSubmit, setFieldValue, errors, touched }) => (
					<CerebroForm onSubmit={handleSubmit}>
						<Grid container spacing={3} sx={{ marginTop: 3 }}>
							<Grid item xs={12}>
								<CerebroLabel required id="dealName">
									Deal Name
								</CerebroLabel>
								<Field as={CerebroInput} id="dealName" name="dealName" disabled={isSubmitting} />
								{FormikUtils.IsInvalid('dealName', errors, touched) && (
									<CerebroText variant="error">{errors.dealName}</CerebroText>
								)}
							</Grid>

							<Grid item xs={12}>
								<CerebroLabel htmlFor="topDealChallenge1">
									Top 3 Deal Challenges{' '}
									<CerebroText variant="hint" component="span">
										(for internal Deal Success Story use, not shared externally)
									</CerebroText>
								</CerebroLabel>
								<Field
									as={CerebroTextarea}
									id="topDealChallenge1"
									name="topDealChallenge1"
									rows={2}
									placeholder="Challenge 1"
									disabled={isSubmitting}
								/>
								<Field
									as={CerebroTextarea}
									id="topDealChallenge2"
									name="topDealChallenge2"
									rows={2}
									placeholder="Challenge 2"
									disabled={isSubmitting}
								/>
								<Field
									as={CerebroTextarea}
									id="topDealChallenge3"
									name="topDealChallenge3"
									rows={2}
									placeholder="Challenge 3"
									disabled={isSubmitting}
								/>
							</Grid>

							<Grid item xs={1}>
								<CerebroCheckbox
									value={values.isVerified}
									id="isVerified"
									error={FormikUtils.IsInvalid('isVerified', errors, touched)}
									onClick={() => {
										setFieldValue('isVerified', !values.isVerified);
									}}
								></CerebroCheckbox>
							</Grid>
							<Grid item xs={11}>
								<CerebroLabel htmlFor="isVerified" sx={{ marginLeft: 1, marginTop: 1.5 }}>
									All excluded and reduced fee lenders have been confirmed with the borrower.
								</CerebroLabel>
								{FormikUtils.IsInvalid('isVerified', errors, touched) && (
									<CerebroText variant="error">{errors.isVerified}</CerebroText>
								)}
							</Grid>

							<Grid item xs={12}>
								<CerebroPrimaryButton type="submit" loading={launching} disabled={!values.isVerified}>
									Launch
								</CerebroPrimaryButton>
								<CerebroSecondaryButton onClick={close} sx={{ marginLeft: 1 }}>
									Close
								</CerebroSecondaryButton>
							</Grid>
						</Grid>
					</CerebroForm>
				)}
			</Formik>
		</CerebroDrawer>
	);
};

type DealSuccessStoryFormMutationVariables = FormikUtils.MutateOnSubmitVariables<DealSuccessStoryFormValues>;
interface DealSuccessStoryFormValues {
	dealName: string;
	topDealChallenge1: string;
	topDealChallenge2: string;
	topDealChallenge3: string;
	isVerified: boolean;
}
type DealSuccessStoryFormErrors = FormikErrors<DealSuccessStoryFormValues>;

function validateForm(values: DealSuccessStoryFormValues) {
	const errors: DealSuccessStoryFormErrors = {};
	
	if (!values.dealName || values.dealName.trim() === '') {
		errors.dealName = 'Deal Name is required.';
	}

	if (!values.isVerified) {
		errors.isVerified = 'You must confirm to launch the deal.';
	}

	return errors;
}
